import BackendService from 'BackendService';
import { BackgroundJob } from 'BackgroundJob';
import { useCallback, useEffect, useRef, useState } from 'react';
import AIChatMessage from '../../../models/AIChatMessage';
import { Article, ArticleContent } from '../../../models/Article';
import { GlobalConfiguration } from '../../../models/GlobalConfiguration';
import { ChatHistoryStorage } from '../../../utils/ChatHistoryStorage';
import { ArticleApprovalProps } from './articleApprovalTypes';
import {
  getArticleJobsFromContext,
  getExpandedChatHeight,
  sanitizeArticlePdfFilename,
  shouldLoadArticleContent,
} from './articleApprovalUtils';

const DEFAULT_MODEL = 'MISTRAL-mistral-medium-latest';

export function useArticleApproval({
  article,
  jobContext,
  navigate,
  refreshActions,
}: ArticleApprovalProps) {
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<ArticleContent | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chatHistory, setChatHistory] = useState<AIChatMessage[]>([]);
  const [articleJobs, setArticleJobs] = useState<BackgroundJob[]>([]);
  const [currentJobId, setCurrentJobId] = useState<string | undefined>();
  const [selectedModel, setSelectedModel] = useState('');
  const [globalConfig, setGlobalConfig] = useState<GlobalConfiguration | null>(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [chatInputValue, setChatInputValue] = useState('');
  const [chatWindowCollapsed, setChatWindowCollapsed] = useState(true);
  const [hasRunningJob, setHasRunningJob] = useState(false);

  const scrollToBottom = useCallback(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, []);

  const reloadArticleContent = useCallback(() => {
    new BackendService()
      .getArticleContent(article)
      .then((loadedContent) => {
        setContent(loadedContent);
      })
      .catch((error) => {
        console.error('Failed to reload article content:', error);
      });
  }, [article]);

  const syncArticleJobs = useCallback(() => {
    const jobState = getArticleJobsFromContext(article.id, jobContext.jobs);
    setArticleJobs(jobState.articleJobs);
    setIsProcessing(jobState.isProcessing);
    setCurrentJobId(jobState.currentJobId);
    setHasRunningJob(jobState.hasRunningJob);
    return jobState;
  }, [article.id, jobContext.jobs]);

  useEffect(() => {
    const savedChatHistory = ChatHistoryStorage.getChatHistory(article.id);
    setChatHistory(savedChatHistory);

    new BackendService()
      .getGlobalConfiguration()
      .then((config) => {
        setGlobalConfig(config);
        setSelectedModel(config.defaultDocumentImportContentModel || DEFAULT_MODEL);
      })
      .catch((error) => {
        console.error('Failed to load global configuration:', error);
        setSelectedModel(DEFAULT_MODEL);
      });

    if (shouldLoadArticleContent(article.status)) {
      new BackendService()
        .getArticleContent(article)
        .then((loadedContent) => {
          setContent(loadedContent);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    syncArticleJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only when article identity changes
  }, [article.id, article.status]);

  const prevJobsRef = useRef(jobContext.jobs);
  const prevArticleJobsRef = useRef<BackgroundJob[]>([]);

  useEffect(() => {
    if (prevJobsRef.current !== jobContext.jobs) {
      const jobState = syncArticleJobs();

      const prevRunningJobs = prevArticleJobsRef.current.filter(
        (job) => job.status === 'IN_PROGRESS' || job.status === 'PENDING',
      );
      const currentRunningJobs = jobState.articleJobs.filter(
        (job) => job.status === 'IN_PROGRESS' || job.status === 'PENDING',
      );

      if (prevRunningJobs.length > 0 && currentRunningJobs.length === 0) {
        reloadArticleContent();
      }

      prevJobsRef.current = jobContext.jobs;
      prevArticleJobsRef.current = jobState.articleJobs;
    }
  }, [jobContext.jobs, reloadArticleContent, syncArticleJobs]);

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollToBottom();
    });
  }, [chatHistory.length, isProcessing, scrollToBottom]);

  const publishArticle = useCallback(() => {
    new BackendService()
      .promoteArticleToPublished(article)
      .then(async () => {
        await refreshActions();
        navigate('/allArticles');
      })
      .catch((error: unknown) => {
        console.error('Error publishing article:', error);
      });
  }, [article, navigate, refreshActions]);

  const archiveArticle = useCallback(async () => {
    try {
      await new BackendService().archiveArticle(article.id);
      navigate('/allArticles');
    } catch (error) {
      console.error('Error archiving article:', error);
    }
  }, [article.id, navigate]);

  const unarchiveArticle = useCallback(async () => {
    try {
      await new BackendService().unarchiveArticle(article.id);
      navigate('/allArticles');
    } catch (error) {
      console.error('Error unarchiving article:', error);
    }
  }, [article.id, navigate]);

  const downloadOriginalArticle = useCallback(() => {
    new BackendService()
      .getArticlePdf(article)
      .then((url: string | null) => {
        if (!url) return;

        const title = sanitizeArticlePdfFilename(article.articleLocation, article.id);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${title}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error: unknown) => {
        console.error('Error downloading PDF:', error);
      });
  }, [article]);

  const handleRefinementSubmit = useCallback(
    async (message: string | null, isRegeneration: boolean = false): Promise<void> => {
      let nextChatHistory: AIChatMessage[] = [];
      setIsProcessing(true);

      if (message) {
        const newMessage: AIChatMessage = {
          role: 'user',
          parts: [{ text: message }],
          timestamp: new Date(),
        };

        nextChatHistory = [...chatHistory, newMessage];
        ChatHistoryStorage.saveChatHistory(article.id, nextChatHistory);
      } else if (isRegeneration) {
        ChatHistoryStorage.clearChatHistory(article.id);
        setChatHistory([]);
      } else {
        setIsProcessing(false);
        return;
      }

      try {
        const result = await new BackendService().refineArticleContent(
          article.id,
          message,
          nextChatHistory,
          isRegeneration,
          selectedModel,
        );

        setChatHistory(nextChatHistory);
        setCurrentJobId(result.jobId);
        jobContext.notifyNewJob(result.jobId);
      } catch (error) {
        console.error('Refinement submission failed:', error);
        setChatHistory(nextChatHistory);
        setIsProcessing(false);
      }
    },
    [article.id, chatHistory, jobContext, selectedModel],
  );

  const handleRegenerate = useCallback(() => {
    void handleRefinementSubmit(null, true);
    setChatInputValue('');
  }, [handleRefinementSubmit]);

  const runningJob = articleJobs.find(
    (job) => job.status === 'IN_PROGRESS' || job.status === 'PENDING',
  );

  return {
    loading,
    content,
    isProcessing,
    chatHistory,
    articleJobs,
    currentJobId,
    globalConfig,
    helpModalOpen,
    chatInputValue,
    chatWindowCollapsed,
    hasRunningJob,
    selectedModel,
    runningJob,
    chatMessagesRef,
    expandedChatHeight: getExpandedChatHeight(),
    setHelpModalOpen,
    setChatInputValue,
    setChatWindowCollapsed,
    setSelectedModel,
    publishArticle,
    archiveArticle,
    unarchiveArticle,
    downloadOriginalArticle,
    handleRefinementSubmit,
    handleRegenerate,
  };
}
